import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, EyeOff, MessageSquareWarning, Paperclip } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type Attachment, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { AttachmentList } from '../components/Attachments';
import { FormPage } from '../components/FormPage';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
  Tr,
  statusTone,
  useToast,
} from '../components/ui';
import { clientName } from './ClientCrs';

/**
 * Work › Client complaints — what clients said, on the client portal, about
 * someone on their projects (operator, 2026-10-28). Each one went to the
 * administrators and to the leaders of that person's departments — about a team
 * leader, to the administrators only — and whoever it went to settles it: New →
 * In review → Closed, with an answer the client reads. The person it is about
 * never sees it; the API finds nothing for them.
 */

export type ComplaintStatus = 'NEW' | 'IN_REVIEW' | 'CLOSED';
export const COMPLAINT_STATUSES: ComplaintStatus[] = ['NEW', 'IN_REVIEW', 'CLOSED'];

interface PersonRef {
  id: string;
  fullName: string;
  fullNameAr: string | null;
}

interface ComplaintRow {
  id: string;
  number: string;
  title: string;
  status: ComplaintStatus;
  aboutLeader: boolean;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  client: { id: string; code: string; companyName: string; companyNameAr: string | null };
  /** Whom it is about; none once they were deleted for good. */
  subject: (PersonRef & { jobTitle: string | null }) | null;
  reviewedBy: PersonRef | null;
  _count: { attachments: number };
}

export interface ComplaintDetail extends ComplaintRow {
  details: string | null;
  /** The sign-in that made it; a contact's names them. */
  account: { id: string; username: string; contact: { name: string; jobTitle: string | null } | null };
  subject:
    | (PersonRef & {
        jobTitle: string | null;
        departments: Array<{ id: string; name: string; nameAr: string | null; memberType: 'MEMBER' | 'LEADER' }>;
        projects: Array<{ id: string; code: string; name: string; nameAr: string | null }>;
      })
    | null;
  attachments: Attachment[];
  /** The team leaders it was sent to, as they were when it was made. */
  leaders: PersonRef[];
  events: Array<{
    id: string;
    fromStatus: ComplaintStatus | null;
    toStatus: ComplaintStatus;
    note: string | null;
    occurredAt: string;
    actor: PersonRef | null;
  }>;
  nextStatuses: ComplaintStatus[];
}

/** A complaint's status in the reader's language — a complaint is feminine in Arabic. */
export function complaintStatusLabel(t: (key: TranslationKey) => string, status: ComplaintStatus): string {
  return t(`complaints.status.${status}` as TranslationKey);
}

/** The button for a move: taking a closed one back into review is reopening it. */
function moveLabel(t: (key: TranslationKey) => string, from: ComplaintStatus, to: ComplaintStatus): string {
  return from === 'CLOSED' && to === 'IN_REVIEW' ? t('complaints.reopen') : t(`complaints.action.${to}` as TranslationKey);
}

export function ClientComplaintsPage() {
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = COMPLAINT_STATUSES.includes(params.get('status') as ComplaintStatus)
    ? (params.get('status') as ComplaintStatus)
    : '';
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['client-complaints', status, page, q],
    queryFn: () =>
      api.get<Paginated<ComplaintRow> & { counts: Record<ComplaintStatus, number> }>('/client-complaints', {
        status: status || undefined,
        q: q.trim() || undefined,
        page,
        pageSize: 25,
      }),
  });

  const counts = data?.counts;
  const all = counts ? COMPLAINT_STATUSES.reduce((sum, s) => sum + (counts[s] ?? 0), 0) : undefined;

  return (
    <>
      <PageHeader
        title={t('complaints.title')}
        subtitle={t(can(P.CLIENT_COMPLAINTS_MANAGE) ? 'complaints.subtitle' : 'complaints.subtitleLeader')}
        count={data?.total}
      />

      <Tabs
        active={status || 'all'}
        onChange={(id) => {
          setPage(1);
          setParams(id === 'all' ? {} : { status: id }, { replace: true });
        }}
        tabs={[
          { id: 'all', label: t('common.all'), count: all },
          ...COMPLAINT_STATUSES.map((s) => ({ id: s, label: complaintStatusLabel(t, s), count: counts?.[s] })),
        ]}
      />

      <Card className="mt-4" padded={false}>
        <div className="p-4">
          <Input
            placeholder={t('complaints.searchPlaceholder')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="max-w-sm"
          />
        </div>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<MessageSquareWarning className="h-8 w-8" />}
            title={t('complaints.empty')}
            hint={t('complaints.emptyHint')}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('complaints.number')}</Th>
                  <Th>{t('complaints.titleField')}</Th>
                  <Th>{t('complaints.about')}</Th>
                  <Th>{t('complaints.client')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('complaints.files')}</Th>
                  <Th align="end">{t('common.createdAt')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((complaint) => (
                  <Tr key={complaint.id} onClick={() => navigate(`/client-complaints/${complaint.id}`)}>
                    <Td>
                      <span className="whitespace-nowrap font-mono text-mono-sm text-primary-600 dark:text-primary-400">
                        {complaint.number}
                      </span>
                    </Td>
                    <Td className="max-w-md">
                      <span className="block truncate" dir="auto">
                        {complaint.title}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap">
                      {complaint.subject ? (
                        <>
                          <span className="block">{localized(complaint.subject)}</span>
                          {complaint.subject.jobTitle && (
                            <span className="block text-caption text-faint">{complaint.subject.jobTitle}</span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">{t('complaints.formerEmployee')}</span>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap text-muted">{clientName(complaint.client, locale)}</Td>
                    <Td>
                      <Badge tone={statusTone(complaint.status)}>{complaintStatusLabel(t, complaint.status)}</Badge>
                    </Td>
                    <Td align="end" className="text-muted">
                      <span className="inline-flex items-center gap-1 tabular">
                        <Paperclip className="h-3.5 w-3.5" aria-hidden />
                        {complaint._count.attachments}
                      </span>
                    </Td>
                    <Td align="end" className="whitespace-nowrap text-caption text-muted">
                      {formatDateTime(complaint.createdAt, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

export function ClientComplaintDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();

  const { data: complaint, isLoading, error, refetch } = useQuery({
    queryKey: ['client-complaint', id],
    queryFn: () => api.get<ComplaintDetail>(`/client-complaints/${id}`),
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !complaint) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const subject = complaint.subject;
  const madeBy = complaint.account.contact;

  return (
    <>
      <div className="mb-4">
        <Link to="/client-complaints" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('nav.clientComplaints')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{complaint.number}</span>
            <span dir="auto">{complaint.title}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(complaint.status)}>{complaintStatusLabel(t, complaint.status)}</Badge>
            <span className="text-muted">
              {clientName(complaint.client, locale)} · {subject ? localized(subject) : t('complaints.formerEmployee')}
            </span>
          </span>
        }
        action={
          complaint.nextStatuses.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {complaint.nextStatuses.map((next) => (
                <Button
                  key={next}
                  variant={next === 'CLOSED' ? 'primary' : 'secondary'}
                  onClick={() => navigate(`/client-complaints/${id}/status?to=${next}`)}
                >
                  {moveLabel(t, complaint.status, next)}
                </Button>
              ))}
            </div>
          )
        }
      />

      <div className="grid gap-5 xl:grid-cols-3">
        <div className="min-w-0 space-y-5 xl:col-span-2">
          {complaint.reviewNote && (
            <Card className={complaint.status === 'CLOSED' ? 'border-success/35' : 'border-primary/30'}>
              <p className="mb-1 text-caption uppercase text-[var(--text-tertiary)]">
                {t(complaint.status === 'CLOSED' ? 'complaints.answer' : 'complaints.lastNote')}
              </p>
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {complaint.reviewNote}
              </p>
            </Card>
          )}

          <Card>
            <CardHeader title={t('complaints.details')} />
            {complaint.details ? (
              <p className="whitespace-pre-wrap text-body-sm text-[var(--text-primary)]" dir="auto">
                {complaint.details}
              </p>
            ) : (
              <p className="text-body-sm text-muted">—</p>
            )}
            {complaint.attachments.length > 0 && (
              <div className="mt-4 border-t hairline pt-4">
                <p className="mb-2 text-caption uppercase text-[var(--text-tertiary)]">{t('complaints.files')}</p>
                <AttachmentList attachments={complaint.attachments} />
              </div>
            )}
          </Card>

          <p className="flex items-start gap-2 text-caption text-muted">
            <EyeOff className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            {t('complaints.neverTheSubject')}
          </p>
        </div>

        <div className="min-w-0 space-y-5">
          <Card>
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('complaints.about')}>
                {subject ? (
                  <span className="inline-flex flex-col items-end">
                    {can(P.USERS_READ) ? (
                      <Link to={`/employees/${subject.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                        {localized(subject)}
                      </Link>
                    ) : (
                      <span>{localized(subject)}</span>
                    )}
                    {subject.jobTitle && <span className="text-caption text-muted">{subject.jobTitle}</span>}
                  </span>
                ) : (
                  <span className="text-muted">{t('complaints.formerEmployee')}</span>
                )}
              </Detail>
              {subject && subject.departments.length > 0 && (
                <Detail label={t('complaints.departments')}>
                  {subject.departments.map((d) => localized(d)).join(' · ')}
                </Detail>
              )}
              {subject && subject.projects.length > 0 && (
                <Detail label={t('complaints.projects')}>
                  <span className="inline-flex flex-col items-end">
                    {subject.projects.map((p) => (
                      <Link key={p.id} to={`/projects/${p.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                        {localized(p)}
                      </Link>
                    ))}
                  </span>
                </Detail>
              )}
              <Detail label={t('complaints.client')}>
                <Link to={`/clients/${complaint.client.id}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                  {clientName(complaint.client, locale)}
                </Link>
              </Detail>
              <Detail label={t('complaints.madeBy')}>
                {/* One of its contacts with their own sign-in, or the company's own. */}
                <span className="inline-flex flex-col items-end">
                  <span>{madeBy?.name ?? clientName(complaint.client, locale)}</span>
                  <bdi dir="ltr" className="text-caption text-muted">
                    {complaint.account.username}
                  </bdi>
                </span>
              </Detail>
              <Detail label={t('common.createdAt')}>{formatDateTime(complaint.createdAt, locale)}</Detail>
              {complaint.reviewedBy && (
                <Detail label={t('complaints.reviewedBy')}>
                  {localized(complaint.reviewedBy)}
                  {complaint.reviewedAt && (
                    <span className="block text-caption text-faint">{formatDateTime(complaint.reviewedAt, locale)}</span>
                  )}
                </Detail>
              )}
            </dl>
          </Card>

          <Card>
            <CardHeader title={t('complaints.sentTo')} />
            <ul className="space-y-1.5 text-body-sm text-[var(--text-primary)]">
              {complaint.aboutLeader ? (
                <li>
                  {subject
                    ? t('complaints.aboutLeader', { name: localized(subject) })
                    : t('complaints.aboutLeaderGone')}
                </li>
              ) : (
                <>
                  <li>{t('complaints.sentToAdmins')}</li>
                  <li className={complaint.leaders.length ? undefined : 'text-muted'}>
                    {complaint.leaders.length
                      ? t('complaints.sentToLeaders', {
                          names: complaint.leaders.map((l) => localized(l)).join(locale === 'ar' ? '، ' : ', '),
                        })
                      : t('complaints.noLeaders')}
                  </li>
                </>
              )}
            </ul>
          </Card>

          <Card>
            <CardHeader title={t('complaints.history')} />
            <ol className="relative">
              {complaint.events.map((event, index) => (
                <li key={event.id} className="relative flex gap-3 pb-4 last:pb-0">
                  <span className="relative flex flex-col items-center">
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" aria-hidden />
                    {index < complaint.events.length - 1 && <span className="w-px flex-1 bg-[var(--line)]" aria-hidden />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={statusTone(event.toStatus)}>{complaintStatusLabel(t, event.toStatus)}</Badge>
                      <span className="text-caption text-muted">
                        {event.actor
                          ? localized(event.actor)
                          : madeBy
                            ? t('complaints.madeByContact', { name: madeBy.name })
                            : t('complaints.madeByClient')}
                      </span>
                    </div>
                    <p className="mt-0.5 text-caption text-faint tabular">{formatDateTime(event.occurredAt, locale)}</p>
                    {event.note && (
                      <p className="mt-1 whitespace-pre-wrap text-body-sm text-muted" dir="auto">
                        {event.note}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </>
  );
}

/**
 * Route: `/client-complaints/:id/status?to=CLOSED` — moving a complaint on,
 * with the note the client reads beside the new status. A page, like every
 * form in the admin; closing needs the note — it is our answer.
 */
export function ClientComplaintStatusPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existing = useQuery({
    queryKey: ['client-complaint', id],
    queryFn: () => api.get<ComplaintDetail>(`/client-complaints/${id}`),
  });

  const complaint = existing.data;
  const requested = params.get('to') as ComplaintStatus | null;
  const options = complaint?.nextStatuses ?? [];
  const [to, setTo] = useState<ComplaintStatus | ''>('');
  const target: ComplaintStatus | '' = to || (requested && options.includes(requested) ? requested : options[0] ?? '');
  const [note, setNote] = useState('');
  const noteRequired = target === 'CLOSED';

  const save = useMutation({
    mutationFn: () => api.post(`/client-complaints/${id}/status`, { status: target, note: note.trim() || undefined }),
    onSuccess: () => {
      toast.success(t('complaints.updated'));
      void queryClient.invalidateQueries({ queryKey: ['client-complaint', id] });
      void queryClient.invalidateQueries({ queryKey: ['client-complaints'] });
      navigate(`/client-complaints/${id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <FormPage
      title={complaint ? `${complaint.number} — ${t('complaints.changeStatus')}` : t('complaints.changeStatus')}
      subtitle={
        complaint
          ? `${complaint.title} · ${clientName(complaint.client, locale)} · ${
              complaint.subject ? localized(complaint.subject) : t('complaints.formerEmployee')
            }`
          : undefined
      }
      backTo={`/client-complaints/${id}`}
      backLabel={complaint?.number ?? t('nav.clientComplaints')}
      submitLabel={complaint && target ? moveLabel(t, complaint.status, target) : t('common.save')}
      submitting={save.isPending}
      disabled={!target || (noteRequired && !note.trim())}
      loading={existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <div className="space-y-4">
        <Field label={t('complaints.moveTo')} required>
          <Select value={target} onChange={(e) => setTo(e.target.value as ComplaintStatus)}>
            {options.map((status) => (
              <option key={status} value={status}>
                {complaintStatusLabel(t, status)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('complaints.reviewNote')}
          required={noteRequired}
          hint={noteRequired ? t('complaints.reviewNoteRequired') : t('complaints.reviewNoteHint')}
        >
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[120px]" dir="auto" autoFocus />
        </Field>
      </div>
    </FormPage>
  );
}
